<template>
  <!-- The day's small drawn glyph on the card's day face (holiday line
       2026-10-30, DESIGN.md §4): the Wheel with today's point lit and softly
       pulsing; the moon for a full moon; a simple emblem for the rest. -->
  <svg class="rc-day-glyph" :class="{ 'is-still': still }" viewBox="0 0 60 60" aria-hidden="true">
    <template v-if="kind === 'wheel'">
      <circle cx="30" cy="30" r="21" fill="none" :stroke="G" stroke-opacity=".55" stroke-width="1" />
      <circle cx="30" cy="30" r="3" :fill="G" fill-opacity=".7" />
      <template v-for="(p, j) in wheel" :key="j">
        <line x1="30" y1="30" :x2="p.x" :y2="p.y" :stroke="G" stroke-opacity=".3" stroke-width=".8" />
        <template v-if="j === lit">
          <circle class="rc-lit-ring" :cx="p.x" :cy="p.y" r="5" fill="none" :stroke="G" stroke-width="1" />
          <circle class="rc-lit" :cx="p.x" :cy="p.y" r="3.6" :fill="G" />
        </template>
        <circle v-else :cx="p.x" :cy="p.y" r="1.9" :fill="G" fill-opacity=".55" />
      </template>
    </template>
    <template v-else-if="kind === 'marigold'">
      <ellipse v-for="j in 14" :key="`o${j}`" cx="30" cy="15.5" rx="4.2" ry="9" fill="none" :stroke="G" stroke-opacity=".75" stroke-width=".9" :transform="`rotate(${j * 360 / 14} 30 30)`" />
      <ellipse v-for="j in 10" :key="`i${j}`" cx="30" cy="21" rx="3" ry="6" :fill="G" fill-opacity=".18" :stroke="G" stroke-opacity=".6" stroke-width=".7" :transform="`rotate(${j * 36 + 18} 30 30)`" />
      <circle cx="30" cy="30" r="4.5" :fill="G" />
    </template>
    <template v-else-if="kind === 'sun'">
      <line v-for="j in 12" :key="j" x1="30" y1="8" x2="30" y2="15" :stroke="G" stroke-opacity=".7" stroke-width="1.2" stroke-linecap="round" :transform="`rotate(${j * 30} 30 30)`" />
      <circle class="rc-lit-ring" cx="30" cy="30" r="13" fill="none" :stroke="G" stroke-opacity=".5" />
      <circle cx="30" cy="30" r="10" :fill="G" />
    </template>
    <template v-else-if="kind === 'eclipse'">
      <circle cx="30" cy="30" r="20" :fill="G" fill-opacity=".12" />
      <circle class="rc-lit-ring" cx="30" cy="30" r="17" fill="none" :stroke="G" stroke-opacity=".8" stroke-width="1.4" />
      <circle cx="30" cy="30" r="15.5" fill="#140a2c" />
    </template>
    <template v-else-if="kind === 'meteor'">
      <line x1="14" y1="16" x2="38" y2="40" :stroke="G" stroke-opacity=".75" stroke-width="1.4" stroke-linecap="round" />
      <line x1="26" y1="10" x2="44" y2="28" :stroke="G" stroke-opacity=".45" stroke-width="1" stroke-linecap="round" />
      <line x1="10" y1="30" x2="24" y2="44" :stroke="G" stroke-opacity=".35" stroke-width=".9" stroke-linecap="round" />
      <circle class="rc-lit-ring" cx="40" cy="42" r="4" fill="none" :stroke="G" stroke-opacity=".5" />
      <circle cx="40" cy="42" r="2.6" :fill="G" />
    </template>
    <template v-else-if="kind === 'lamp'">
      <path d="M14 38 Q30 50 46 38 Q38 34 30 34 Q22 34 14 38 Z" fill="none" :stroke="G" stroke-opacity=".8" stroke-width="1.1" />
      <path class="rc-lit-ring" d="M30 14 Q37 24 30 32 Q23 24 30 14 Z" :fill="G" fill-opacity=".35" :stroke="G" stroke-width=".8" />
      <path d="M30 20 Q34 26 30 31 Q26 26 30 20 Z" :fill="G" />
    </template>
    <template v-else-if="kind === 'lantern'">
      <line x1="30" y1="6" x2="30" y2="13" :stroke="G" stroke-opacity=".7" />
      <rect x="24" y="12" width="12" height="3" rx="1" :fill="G" fill-opacity=".8" />
      <ellipse class="rc-lit-ring" cx="30" cy="30" rx="14" ry="15" :fill="G" fill-opacity=".2" :stroke="G" stroke-opacity=".8" />
      <path d="M24 16 Q20 30 24 44 M36 16 Q40 30 36 44 M30 15 V45" fill="none" :stroke="G" stroke-opacity=".5" stroke-width=".8" />
      <rect x="24" y="44" width="12" height="3" rx="1" :fill="G" fill-opacity=".8" />
      <line x1="30" y1="47" x2="30" y2="54" :stroke="G" stroke-opacity=".6" />
    </template>
    <template v-else-if="kind === 'color'">
      <circle cx="22" cy="24" r="9" fill="#e7a6d6" fill-opacity=".55" />
      <circle cx="37" cy="23" r="8" fill="#f0d9a3" fill-opacity=".6" />
      <circle cx="30" cy="37" r="10" fill="#a9c7ff" fill-opacity=".45" />
      <circle class="rc-lit-ring" cx="30" cy="30" r="22" fill="none" :stroke="G" stroke-opacity=".35" />
    </template>
    <template v-else-if="kind === 'candles'">
      <rect v-for="j in 5" :key="j" :x="8 + (j - 1) * 10" y="30" width="4" height="18" rx="1" :fill="G" fill-opacity=".55" />
      <path v-for="j in 5" :key="`f${j}`" :d="`M${10 + (j - 1) * 10} 21 q3 4 0 7 q-3 -3 0 -7 z`" :fill="G" :class="{ 'rc-lit-ring': j === 3 }" />
      <line x1="6" y1="49" x2="54" y2="49" :stroke="G" stroke-opacity=".6" />
    </template>
    <template v-else-if="kind === 'sprout'">
      <path d="M30 50 V28" :stroke="G" stroke-opacity=".8" stroke-width="1.3" stroke-linecap="round" />
      <path d="M30 34 Q18 32 15 20 Q27 20 30 34 Z" :fill="G" fill-opacity=".45" :stroke="G" stroke-width=".8" />
      <path class="rc-lit-ring" d="M30 30 Q42 26 45 14 Q33 15 30 30 Z" :fill="G" fill-opacity=".6" :stroke="G" stroke-width=".8" />
      <path d="M18 50 H42" :stroke="G" stroke-opacity=".5" />
    </template>
    <template v-else>
      <!-- the full moon -->
      <circle cx="30" cy="30" r="25" :fill="G" fill-opacity=".08" />
      <circle class="rc-lit-ring" cx="30" cy="30" r="19" fill="none" :stroke="G" stroke-opacity=".5" />
      <circle cx="30" cy="30" r="17" :fill="G" />
      <circle cx="24" cy="26" r="3.2" fill="#c9a96a" fill-opacity=".55" />
      <circle cx="35" cy="34" r="4.4" fill="#c9a96a" fill-opacity=".45" />
      <circle cx="33" cy="22" r="2" fill="#c9a96a" fill-opacity=".5" />
    </template>
  </svg>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  kind: { type: String, default: 'fullmoon' },
  // The Wheel's lit point, 0 = Yule at the top, clockwise.
  at: { type: Number, default: -1 },
  // Reduced motion: no pulse.
  still: { type: Boolean, default: false },
})
const G = '#f0d9a3'
const wheel = computed(() => Array.from({ length: 8 }, (_, j) => {
  const a = (-90 + j * 45) * Math.PI / 180
  return { x: +(30 + 21 * Math.cos(a)).toFixed(2), y: +(30 + 21 * Math.sin(a)).toFixed(2) }
}))
const lit = computed(() => (Number.isInteger(props.at) ? props.at : -1))
</script>

<style scoped>
.rc-day-glyph { display: block; width: 46px; height: 46px; margin: 8px auto 4px; overflow: visible; }
.rc-lit-ring { transform-origin: center; transform-box: fill-box; animation: rc-lit-pulse 3.2s ease-in-out infinite; }
@keyframes rc-lit-pulse {
  0%, 100% { opacity: 0.35; transform: scale(1); }
  50% { opacity: 0.9; transform: scale(1.5); }
}
.rc-day-glyph.is-still .rc-lit-ring { animation: none; }
@media (prefers-reduced-motion: reduce) {
  .rc-lit-ring { animation: none; }
}
</style>
